import { RefObject, useCallback, useRef, useState } from 'react';
import { idsIntersectingMarquee, Bounds } from '../marqueeModel';

export interface MarqueeRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

const DRAG_THRESHOLD_PX = 4;

/**
 * Rubber-band (marquee) selection over empty grid space.
 *
 * Coexists with dnd-kit drag: we only start a marquee when the pointer goes down
 * on the container itself (empty space) — pointer-downs on cards are left to
 * dnd-kit's draggable listeners. Card hit-testing reads `[data-entry-id]` rects
 * live during the drag, so selection updates as the box grows.
 *
 * `data-entry-id` and not `data-file-key`: the marquee feeds the selection set,
 * which is keyed on entry identity (STD-029 R-2). The card still carries
 * `data-file-key` as its display path — that is what browser specs address
 * cards by, and it is deliberately NOT the identity.
 *
 * Rect coordinates are viewport-relative (clientX/Y) so the overlay can render
 * with `position: fixed`.
 */
export function useMarquee(
  containerRef: RefObject<HTMLElement>,
  onSelect: (ids: string[]) => void,
  /** Called once on pointer-up if a real drag happened — lets the caller suppress the trailing click. */
  onMarqueeEnd: () => void
): { marqueeRect: MarqueeRect | null; onPointerDown: (e: React.PointerEvent) => void } {
  const [rect, setRect] = useState<MarqueeRect | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const movedRef = useRef(false);

  const hitTest = useCallback(
    (x1: number, y1: number, x2: number, y2: number) => {
      const container = containerRef.current;
      if (!container) return;
      const cards: { id: string; bounds: Bounds }[] = [];
      container.querySelectorAll<HTMLElement>('[data-entry-id]').forEach((el) => {
        const id = el.getAttribute('data-entry-id');
        if (!id) return;
        const r = el.getBoundingClientRect();
        cards.push({ id, bounds: { left: r.left, top: r.top, right: r.right, bottom: r.bottom } });
      });
      const marquee: Bounds = { left: x1, top: y1, right: x2, bottom: y2 };
      onSelect(idsIntersectingMarquee(marquee, cards));
    },
    [containerRef, onSelect]
  );

  const handleMove = useCallback(
    (e: PointerEvent) => {
      const start = startRef.current;
      if (!start) return;
      const x1 = Math.min(start.x, e.clientX);
      const x2 = Math.max(start.x, e.clientX);
      const y1 = Math.min(start.y, e.clientY);
      const y2 = Math.max(start.y, e.clientY);
      if (
        Math.abs(e.clientX - start.x) > DRAG_THRESHOLD_PX ||
        Math.abs(e.clientY - start.y) > DRAG_THRESHOLD_PX
      ) {
        movedRef.current = true;
      }
      setRect({ left: x1, top: y1, width: x2 - x1, height: y2 - y1 });
      if (movedRef.current) hitTest(x1, y1, x2, y2);
    },
    [hitTest]
  );

  const handleUp = useCallback(() => {
    window.removeEventListener('pointermove', handleMove);
    window.removeEventListener('pointerup', handleUp);
    // Also clean up on pointercancel (touch gesture / OS overlay / capture loss),
    // otherwise the window listeners leak and a stale startRef corrupts the next drag.
    window.removeEventListener('pointercancel', handleUp);
    startRef.current = null;
    setRect(null);
    if (movedRef.current) onMarqueeEnd();
    movedRef.current = false;
  }, [handleMove, onMarqueeEnd]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Left button only, and only on the empty container (not a card).
      if (e.button !== 0 || e.target !== e.currentTarget) return;
      startRef.current = { x: e.clientX, y: e.clientY };
      movedRef.current = false;
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
    },
    [handleMove, handleUp]
  );

  return { marqueeRect: rect, onPointerDown };
}
